import { CheckOutlined } from "@ant-design/icons";
import { featureLabels, plans } from "components/dashboard/home/constants";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "redux/store";
import Button from "style-guide/component/Button";
import Typography from "style-guide/component/Typography";
import DemoRequestModal from "./DemoRequestModal";
import { useState } from "react";

export type Plan =
  | "Get Started"
  | "Start your Pro trial"
  | "Request Demo"
  | "Talk to Sales";

export const PricingTable: React.FC = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<"demo" | "sales">("demo");

  const { userProfile } = useSelector(
    (state: RootState) => state.applicationData
  );

  const userTier = userProfile?.userDetails[0]?.subscription;
  const isBuyCreditProfessional =
    userProfile?.userDetails[0]?.isBuyCreditProfessional;

  const handlePlanClick = (plan: (typeof plans)[0]) => {
    if (
      (plan.name === userTier &&
        !(plan.name === "Professional" && isBuyCreditProfessional)) ||
      plan.name === "Starter"
    ) {
      return;
    }

    // Handle modal-based plans
    if (plan.cta === "Request Demo") {
      setModalType("demo");
      setIsModalOpen(true);
      return;
    }

    if (plan.cta === "Talk to Sales") {
      setModalType("sales");
      setIsModalOpen(true);
      return;
    }

    // Handle checkout plans
    navigate("/checkout", {
      state: {
        planDetails: plan,
      },
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div className="flex flex-col gap-4 md:gap-6 lg:gap-8 py-4 md:py-6">
      {/* Header */}
      <div className="flex justify-center">
        <Typography 
          variant="Heading 2" 
          text="Upgrade your plan" 
          strong 
          className="text-xl sm:text-2xl lg:text-3xl text-center"
        />
      </div>

      {/* Mobile & Tablet View - Stacked Cards (< 1024px) */}
      <div className="lg:hidden">
        <div className="flex flex-col gap-4 sm:gap-5">
          {plans.map((plan) => {
            const isCurrentPlan = plan.name === userTier &&
              !(plan.name === "Professional" && isBuyCreditProfessional);
            
            return (
              <div
                key={plan.name}
                className="bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden border border-gray-200"
                style={{ backgroundColor: plan.bgColorCode }}
              >
                {/* Plan Header */}
                <div className="p-4 sm:p-6">
                  <div className="flex flex-col gap-3 mb-4">
                    <Typography
                      variant="Heading 3"
                      className="!font-medium text-[#0B4EA2] text-lg sm:text-xl"
                    >
                      {plan.name}
                    </Typography>
                    <Typography
                      variant="SM"
                      className="text-black !font-medium text-sm sm:text-base"
                    >
                      {plan.description}
                    </Typography>
                    <Typography
                      variant="Heading 4"
                      className="!font-medium text-[#0B4EA2] text-xl sm:text-2xl"
                    >
                      {plan.monthlyPrice}
                    </Typography>
                  </div>
                  <Button
                    type={isCurrentPlan ? "Disabled" : "Primary"}
                    className="w-full h-11 sm:h-12 text-sm sm:text-base"
                    onClick={() => handlePlanClick(plan)}
                    buttonText={isCurrentPlan ? "Current Plan" : plan.cta}
                  />
                </div>

                {/* Features Section */}
                <div className="border-t border-gray-200 bg-white bg-opacity-50">
                  <div className="p-4 sm:p-6">
                    <h4 className="text-base sm:text-lg font-semibold text-[#0B4EA2] mb-3 sm:mb-4">
                      Core Usage
                    </h4>
                    <div className="space-y-2 sm:space-y-3">
                      {plan.features.map((val, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between py-2 sm:py-2.5 border-b border-gray-100 last:border-b-0"
                        >
                          <span className="text-xs sm:text-sm text-gray-700 flex-1 pr-3">
                            {featureLabels[idx]}
                          </span>
                          <div className="ml-2 flex-shrink-0 min-w-[60px] sm:min-w-[80px] text-right">
                            {typeof val === "boolean" ? (
                              val ? (
                                <CheckOutlined className="text-[#0B4EA2] text-base sm:text-lg" />
                              ) : (
                                <span className="text-gray-300 text-sm">—</span>
                              )
                            ) : (
                              <Typography
                                variant="LG"
                                className="!font-semibold text-gray-900 text-xs sm:text-sm"
                              >
                                {val}
                              </Typography>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Desktop View - Table Layout (≥ 1024px) */}
      <div className="hidden lg:block">
        <div className="flex gap-4 xl:gap-6 items-start">
          {/* Left side - Feature Labels (Fixed) */}
          <div className="bg-white flex-shrink-0 w-48 xl:w-56 sticky left-0 z-10">
            {/* Empty space to match plan card header */}
            <div className="h-[280px] xl:h-[300px]" />
            
            {/* Core Usage Header */}
            <div className="min-h-[56px] flex items-center">
              <h3 className="text-base xl:text-lg font-semibold text-[#0B4EA2]">
                Core Usage
              </h3>
            </div>

            {/* Feature Labels */}
            {featureLabels.map((label, idx) => (
              <div
                key={idx}
                className={`text-start flex items-center justify-start min-h-[56px] pr-4 text-sm xl:text-base ${
                  idx === 2 || idx === 10 ? "border-b-8 border-blue-200" : ""
                }`}
              >
                {label}
              </div>
            ))}
          </div>

          {/* Right side - Plan Cards (Scrollable) */}
          <div className="flex-1 overflow-x-auto">
            <div className="grid grid-cols-4 gap-4 xl:gap-6 min-w-[800px]">
              {plans.map((plan) => {
                const isCurrentPlan = plan.name === userTier &&
                  !(plan.name === "Professional" && isBuyCreditProfessional);

                return (
                  <div
                    key={plan.name}
                    className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200"
                    style={{ backgroundColor: plan.bgColorCode }}
                  >
                    {/* Plan Header - Fixed height */}
                    <div className="p-3 xl:p-4 text-start flex flex-col justify-between h-[280px] xl:h-[300px]">
                      <div className="flex flex-col gap-2 xl:gap-3">
                        <Typography
                          variant="Heading 3"
                          className="!font-semibold text-[#0B4EA2] min-h-12 xl:min-h-16 text-base xl:text-lg leading-tight"
                        >
                          {plan.name}
                        </Typography>
                        <Typography
                          variant="SM"
                          className="text-black !font-medium min-h-8 xl:min-h-10 text-xs xl:text-sm leading-snug"
                        >
                          {plan.description}
                        </Typography>
                      </div>
                      <div className="flex flex-col gap-3 xl:gap-4">
                        <div className="min-h-[50px] xl:min-h-[60px]">
                          <Typography
                            variant="Heading 4"
                            className="!font-semibold text-[#0B4EA2] text-lg xl:text-xl"
                          >
                            {plan.monthlyPrice}
                          </Typography>
                        </div>
                        <Button
                          type={isCurrentPlan ? "Disabled" : "Primary"}
                          className="w-full h-9 xl:h-10 text-xs xl:text-sm"
                          onClick={() => handlePlanClick(plan)}
                          buttonText={isCurrentPlan ? "Current Plan" : plan.cta}
                        />
                      </div>
                    </div>

                    {/* Core Usage Header - Empty spacer */}
                    <div className="min-h-[56px] flex items-center justify-center" />

                    {/* Plan Features - Aligned with labels */}
                    {plan.features.map((val, idx) => (
                      <div
                        key={idx}
                        className={`text-center flex items-center justify-center min-h-[56px] px-2 ${
                          idx === 2 || idx === 10
                            ? "border-b-8 border-blue-200"
                            : ""
                        }`}
                      >
                        {typeof val === "boolean" ? (
                          val ? (
                            <CheckOutlined className="text-[#0B4EA2] text-base xl:text-lg" />
                          ) : null
                        ) : (
                          <Typography
                            variant="LG"
                            className="!font-semibold text-gray-900 text-xs xl:text-sm"
                          >
                            {val}
                          </Typography>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Demo Request Modal */}
      <DemoRequestModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        modalType={modalType}
      />
    </div>
  );
};